"use client";

import Link from "next/link";
import { ArrowLeft, FlaskConical, ChevronDown, ExternalLink } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { GradeBadge } from "@/components/common/grade-badge";
import { Badge } from "@/components/ui/badge";
import type { EvidenceResponse, PurposeArticleGroup, EvidenceArticle } from "@/types/api";
import { wikiLink } from "@/lib/url";

const gradeLabels: Record<string, string> = {
  A: "근거 풍부",
  B: "근거 양호",
  C: "근거 제한적",
  D: "근거 부족",
};

const typeLabels: Record<string, { label: string; color: string }> = {
  "meta-analysis": { label: "메타", color: "bg-green-100 text-green-700" },
  rct: { label: "RCT", color: "bg-blue-100 text-blue-700" },
  "clinical-trial": { label: "임상", color: "bg-amber-100 text-amber-700" },
  observational: { label: "관찰", color: "bg-indigo-100 text-indigo-700" },
  "case-report": { label: "증례", color: "bg-pink-100 text-pink-700" },
};

interface EvidenceDetailProps {
  data: EvidenceResponse;
}

export function EvidenceDetail({ data }: EvidenceDetailProps) {
  const router = useRouter();
  const { procedure, evidence, groups, articles } = data;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 px-5 duration-400">
      {/* 헤더 */}
      <div className="py-6">
        <button
          onClick={() => router.back()}
          className="text-muted-foreground hover:text-primary mb-3 inline-flex items-center gap-1 text-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 뒤로
        </button>
        <div className="mb-2 flex items-center gap-3">
          <FlaskConical className="text-primary h-5 w-5" />
          <h1 className="text-2xl font-extrabold tracking-tight">{procedure.title} 임상 근거</h1>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <GradeBadge grade={evidence.evidence_grade} size="lg" />
          <div>
            <span className="text-base font-bold">{gradeLabels[evidence.evidence_grade]}</span>
            <span className="text-muted-foreground block text-xs">
              논문 {evidence.total_articles.toLocaleString()}편 기반
              {evidence.brand_matched_count > 0 &&
                ` · 브랜드 직접 언급 ${evidence.brand_matched_count}편`}
            </span>
          </div>
        </div>
      </div>

      {/* 적응증별 그룹 */}
      {groups?.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base font-bold">적응증별 근거</h2>
          <div className="flex flex-col gap-2">
            {groups.map((group) => (
              <PurposeGroup key={group.purpose} group={group} />
            ))}
          </div>
        </section>
      )}

      {/* 전체 논문 목록 (상위 20개) */}
      {articles?.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base font-bold">주요 논문 ({articles.length}편)</h2>
          <div className="flex flex-col gap-1">
            {articles.slice(0, 20).map((a) => (
              <ArticleRow key={a.pmid} article={a} />
            ))}
          </div>
          {articles.length > 20 && (
            <p className="text-muted-foreground mt-3 text-center text-xs">
              외 {articles.length - 20}편
            </p>
          )}
        </section>
      )}

      {/* 시술 페이지 링크 */}
      <div className="flex justify-center pb-8">
        <Link
          href={wikiLink(procedure.title)}
          className="text-foreground hover:border-primary hover:text-primary inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-semibold transition-colors"
        >
          {procedure.title} 시술 상세 보기
        </Link>
      </div>
    </div>
  );
}

function PurposeGroup({ group }: { group: PurposeArticleGroup }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`overflow-hidden rounded-xl border transition-colors ${open ? "border-primary" : "border-gray-200"}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 bg-gray-50 p-4 text-left transition-colors hover:bg-violet-50"
      >
        <span className="text-sm font-bold">{group.purpose}</span>
        {group.evidence_grade && <GradeBadge grade={group.evidence_grade} size="sm" />}
        <span className="text-muted-foreground text-xs">{group.article_count}편</span>
        <ChevronDown
          className={`text-muted-foreground ml-auto h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && group.articles?.length > 0 && (
        <div className="flex flex-col gap-1 p-3">
          {group.articles.map((a) => (
            <ArticleRow key={a.pmid} article={a} compact />
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ article, compact }: { article: EvidenceArticle; compact?: boolean }) {
  const typeInfo = typeLabels[article.article_type] || {
    label: article.article_type,
    color: "bg-gray-100 text-gray-600",
  };

  return (
    <div
      className={`flex items-start gap-2 ${compact ? "py-2" : "border-b border-gray-100 py-3 last:border-0"}`}
    >
      <Badge className={`shrink-0 rounded border-0 text-[10px] font-bold ${typeInfo.color}`}>
        {typeInfo.label}
      </Badge>
      <div className="min-w-0 flex-1">
        <a
          href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-primary text-sm leading-snug transition-colors"
        >
          {article.title_ko || article.title}
          <ExternalLink className="text-muted-foreground ml-1 inline h-3 w-3" />
        </a>
        <span className="text-muted-foreground mt-0.5 block text-xs">
          {article.journal} · {article.year}
          {article.n_subjects > 0 && ` · n=${article.n_subjects}`}
        </span>
      </div>
      {article.brand_match && (
        <Badge className="text-primary shrink-0 rounded-full border-0 bg-violet-50 text-[10px] font-semibold">
          브랜드
        </Badge>
      )}
    </div>
  );
}
